'use client';

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { ModelDisplay, VideoDisplay } from '@/shared';
import { CUP_ORDER } from '@/constants';
import { MeasurementFilters, MeasurementRange, EMPTY_MEASUREMENTS } from '@/components/private/actions/list';
import { getParam, getList, setParam } from '@/url-state';

interface SearchContextValue {
  allVideos: VideoDisplay[] | null;
  allModels: ModelDisplay[] | null;
  dataLoading: boolean;

  pageQuery: string;
  videoSort: string;
  modelSort: string;
  selectedVideoTags: string[];
  selectedModelTags: string[];
  measurementFilters: MeasurementFilters;

  setPageQuery: (q: string) => void;
  setVideoSort: (s: string) => void;
  setModelSort: (s: string) => void;
  toggleVideoTag: (tag: string) => void;
  removeVideoTag: (tag: string) => void;
  toggleModelTag: (tag: string) => void;
  removeModelTag: (tag: string) => void;
  setMeasurementFilter: (field: keyof MeasurementFilters, range: MeasurementRange) => void;
  clearMeasurementFilters: () => void;
}

const SearchContext = createContext<SearchContextValue>({
  allVideos: null,
  allModels: null,
  dataLoading: true,
  pageQuery: ``,
  videoSort: ``,
  modelSort: ``,
  selectedVideoTags: [],
  selectedModelTags: [],
  measurementFilters: EMPTY_MEASUREMENTS,
  setPageQuery: () => {},
  setVideoSort: () => {},
  setModelSort: () => {},
  toggleVideoTag: () => {},
  removeVideoTag: () => {},
  toggleModelTag: () => {},
  removeModelTag: () => {},
  setMeasurementFilter: () => {},
  clearMeasurementFilters: () => {},
});

async function fetchAllPages<T>(
  endpoint: string,
  signal: AbortSignal,
  onPage: (items: T[]) => void
): Promise<void> {
  let page = 1;
  const pageSize = 100;

  while (true) {
    const res = await fetch(
      `${endpoint}?options=${JSON.stringify({ pagination: { page, pageSize, }, })}`,
      { signal, }
    );
    const json = await res.json();
    const d = json.data;
    onPage(d.data);
    if (d.page >= d.pageCount) break;
    page++;
  }
}

export function SearchProvider({ children, }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [allVideos, setAllVideos,] = useState<VideoDisplay[] | null>(null);
  const [allModels, setAllModels,] = useState<ModelDisplay[] | null>(null);
  const [dataLoading, setDataLoading,] = useState(true);

  const [pageQuery, setPageQueryState,] = useState(``);
  const [videoSort, setVideoSortState,] = useState(``);
  const [modelSort, setModelSortState,] = useState(``);
  const [selectedVideoTags, setSelectedVideoTags,] = useState<string[]>([]);
  const [selectedModelTags, setSelectedModelTags,] = useState<string[]>([]);
  const [measurementFilters, setMeasurementFilters,] = useState<MeasurementFilters>(EMPTY_MEASUREMENTS);

  // Read URL params on each route change
  useEffect(() => {
    setPageQueryState(getParam(`q`) || ``);
    if (pathname === `/videos`) {
      setVideoSortState(getParam(`vs`) || ``);
      setSelectedVideoTags(getList(`vt`));
    } else if (pathname === `/models`) {
      setModelSortState(getParam(`ms`) || ``);
      setSelectedModelTags(getList(`mt`));
      const mf = getParam(`mf`);
      if (mf) {
        const parsed = { ...EMPTY_MEASUREMENTS, };
        for (const part of mf.split(`,`)) {
          const [field, range,] = part.split(`:`);
          if (!range) continue;
          const [minStr, maxStr,] = range.split(`-`);
          if (field === `cup`) {
            const minIdx = CUP_ORDER.indexOf(minStr);
            const maxIdx = CUP_ORDER.indexOf(maxStr);
            if (minIdx !== -1 && maxIdx !== -1) parsed.cup = [minIdx, maxIdx,];
          } else if (field in parsed) {
            const min = parseInt(minStr, 10);
            const max = parseInt(maxStr, 10);
            if (!isNaN(min) && !isNaN(max)) (parsed as any)[field] = [min, max,];
          }
        }
        setMeasurementFilters(parsed);
      } else {
        setMeasurementFilters(EMPTY_MEASUREMENTS);
      }
    }
  }, [pathname,]);

  // Load all data once on mount
  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;

    const controller = new AbortController();

    async function load() {
      await Promise.all([
        fetchAllPages<VideoDisplay>(`/api/videos`, controller.signal, items =>
          setAllVideos(prev => [...(prev ?? []), ...items,])
        ),
        fetchAllPages<ModelDisplay>(`/api/models`, controller.signal, items =>
          setAllModels(prev => [...(prev ?? []), ...items,])
        ),
      ]);
      setDataLoading(false);
    }

    load().catch(err => console.error(`SearchContext: failed to load data`, err));
    return () => controller.abort();
  }, []);

  function setPageQuery(q: string) {
    setPageQueryState(q);
    setParam(`q`, q || null);
  }

  function setVideoSort(s: string) {
    setVideoSortState(s);
    setParam(`vs`, s || null);
  }

  function setModelSort(s: string) {
    setModelSortState(s);
    setParam(`ms`, s || null);
  }

  function toggleVideoTag(tag: string) {
    const next = selectedVideoTags.includes(tag) ? selectedVideoTags : [...selectedVideoTags, tag,];
    setSelectedVideoTags(next);
    setParam(`vt`, next.length ? next.join(`,`) : null);
  }

  function removeVideoTag(tag: string) {
    const next = selectedVideoTags.filter(t => t !== tag);
    setSelectedVideoTags(next);
    setParam(`vt`, next.length ? next.join(`,`) : null);
  }

  function toggleModelTag(tag: string) {
    const next = selectedModelTags.includes(tag) ? selectedModelTags : [...selectedModelTags, tag,];
    setSelectedModelTags(next);
    setParam(`mt`, next.length ? next.join(`,`) : null);
  }

  function syncMeasurements(filters: MeasurementFilters) {
    const parts: string[] = [];
    for (const [key, val,] of Object.entries(filters)) {
      if (!val) continue;
      const [min, max,] = val;
      if (key === `cup`) {
        parts.push(`cup:${CUP_ORDER[min]}-${CUP_ORDER[max]}`);
      } else {
        parts.push(`${key}:${min}-${max}`);
      }
    }
    setParam(`mf`, parts.length ? parts.join(`,`) : null);
  }

  function setMeasurementFilter(field: keyof MeasurementFilters, range: MeasurementRange) {
    const next = { ...measurementFilters, [field]: range, };
    setMeasurementFilters(next);
    syncMeasurements(next);
  }

  function clearMeasurementFilters() {
    setMeasurementFilters(EMPTY_MEASUREMENTS);
    syncMeasurements(EMPTY_MEASUREMENTS);
  }

  function removeModelTag(tag: string) {
    const next = selectedModelTags.filter(t => t !== tag);
    setSelectedModelTags(next);
    setParam(`mt`, next.length ? next.join(`,`) : null);
  }

  return (
    <SearchContext.Provider value={{
      allVideos,
      allModels,
      dataLoading,
      pageQuery,
      videoSort,
      modelSort,
      selectedVideoTags,
      selectedModelTags,
      setPageQuery,
      setVideoSort,
      setModelSort,
      toggleVideoTag,
      removeVideoTag,
      toggleModelTag,
      removeModelTag,
      measurementFilters,
      setMeasurementFilter,
      clearMeasurementFilters,
    }}>
      {children}
    </SearchContext.Provider>
  );
}

export function useSearch() {
  return useContext(SearchContext);
}
